// cSpell:ignore afgerekend afrekening afrekeningen alleen Betalingen bevestigde concepten Deels Geen groep groepen lijst Opgenomen opgeslagen pand panden Periode vertrek Volgende Wijzigen
// Global costs overview (Rentio Legal sidebar). Lists every invoice across the
// broker account and lets you add a new one assigned to one or more panden/groepen.

import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Card,
  EmptyState,
  Page,
  Stack,
} from "@rentiohq/web-shared/dist/components";
import { ButtonGroup } from "@rentiohq/web-shared/dist/components/ButtonGroup";
import {
  PropertySelectInline,
  Stack as MuiStack,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { FontWeights } from "@rentiohq/web-theme";
import { isEqual } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import { createGlobalStyle } from "styled-components";
import { CostsTable, ICostsTableView } from "./components/CostsTable";
import { InvoiceModal } from "./components/InvoiceModal";
import { ListLoadFailed } from "./components/ListLoadFailed";
import {
  SettlementRowVM,
  SettlementsTable,
} from "./components/SettlementsTable";
import { costsHaveOpenPayment } from "./costPayment.utils";
import {
  costSettledInfo,
  costSettlementStateOf,
  costSettlementStates,
} from "./costSettlementState.utils";
import {
  getListState,
  settlementDeleteBlockedMessage,
  sortSettlementsNewestFirst,
} from "./costsList.utils";
import {
  deleteRefusedMessage,
  settlementTenantPaymentStatus,
  useAssignableProperties,
  useCostPaymentStatusMaps,
  useInvoices,
  useSettlementPaymentStatus,
  useSettlements,
} from "./hooks";
import { roundCents } from "./money.utils";
import { formatMoney } from "./moneyText.utils";
import {
  settlementAmountTexts,
  settlementRowTexts,
} from "./settlementListColumns.utils";
import { paymentRequestPropertyIds } from "./settlementPaymentStatus.utils";
import { CATEGORY_LABELS_NL, Invoice, Settlement } from "./types";
import { useWithArchivedProperties } from "./useArchivedProperties";
import { useCostsEnabled } from "./useCostsEnabled";

// The legacy web-shared Modal sits at z-index 99999; the MUI Autocomplete
// dropdown (PropertySelectInline) defaults far below that and would render
// behind the modal. Lift its portalled popper above the modal while open.
const AutocompletePopperFix = createGlobalStyle`
  .MuiAutocomplete-popper { z-index: 100001 !important; }
`;

const fmt = formatMoney;

const CostsOverviewInner: React.FC = () => {
  const navigate = useNavigate();
  const {
    items: invoices,
    isLoading: isLoadingInvoices,
    loadFailed: invoicesLoadFailed,
    create,
    update,
    remove,
    refetch,
  } = useInvoices({});
  const { items: assignable, isFetching: isFetchingAssignable } =
    useAssignableProperties();
  const {
    items: settlements,
    isLoading: isLoadingSettlements,
    loadFailed: settlementsLoadFailed,
    remove: removeSettlement,
    refetch: refetchSettlements,
  } = useSettlements({});
  // For the labels: a cost or a settlement of a property archived since is
  // still named by its address.
  const labelProperties = useWithArchivedProperties({
    properties: assignable,
    isFetching: isFetchingAssignable,
    ids: [
      ...invoices.map(i => i.unitId ?? i.groupId),
      ...settlements.map(s => s.unitId ?? s.groupId),
    ],
  });
  // Live payment status for the cost list: the tenant payment requests live on
  // the cost's own unit(s), and on the unit each split billed (a group cost
  // asked from the tenants of its units, archived ones too).
  const costPropertyIds = React.useMemo(
    () =>
      paymentRequestPropertyIds({
        // Group costs carry their requests on the group property (bulk)
        // — include both ids so the live status resolves either way.
        propertyIds: invoices.flatMap(i => [i.unitId, i.groupId]),
        costs: invoices,
      }),
    [invoices],
  );
  const costStatusMaps = useCostPaymentStatusMaps(invoices, costPropertyIds);
  const costStatusByOrderId = costStatusMaps.byOrderId;
  const [showModal, setShowModal] = useState(false);
  // What the cost list shows after its search and filters (heading).
  const [costsView, setCostsView] = useState<ICostsTableView | null>(null);
  const handleCostsViewChange = (view: ICostsTableView) =>
    setCostsView(prev => (isEqual(prev, view) ? prev : view));
  const [drawerParams, setDrawerParams] = useQueryParams({
    propertyCostId: StringParam,
    propertyCostEditId: StringParam,
    settlementId: StringParam,
  });
  const openCostDrawer = (inv: Invoice) =>
    setDrawerParams({ propertyCostId: inv.id });
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);

  // The sidebar drawer hands off "Wijzigen" via `propertyCostEditId`. Pick it
  // up and open the existing edit modal once the invoice list is loaded.
  useEffect(() => {
    if (!drawerParams.propertyCostEditId) return;
    const target = invoices.find(i => i.id === drawerParams.propertyCostEditId);
    if (target) {
      setEditInvoice(target);
      setDrawerParams({ propertyCostEditId: undefined });
    }
  }, [drawerParams.propertyCostEditId, invoices, setDrawerParams]);

  // Refetch paged list when the detail drawer closes — the drawer updates via
  // propertyCostPromises.update which only writes to detail[id], not paged.
  const prevCostIdRef = useRef(drawerParams.propertyCostId);
  useEffect(() => {
    const prev = prevCostIdRef.current;
    prevCostIdRef.current = drawerParams.propertyCostId;
    if (prev && !drawerParams.propertyCostId) {
      refetch();
    }
  }, [drawerParams.propertyCostId, refetch]);

  // Same pattern for the settlement drawer: refresh the list once it closes
  // (or after a delete from inside the drawer).
  const prevSettlementIdRef = useRef(drawerParams.settlementId);
  useEffect(() => {
    const prev = prevSettlementIdRef.current;
    prevSettlementIdRef.current = drawerParams.settlementId;
    if (prev && !drawerParams.settlementId) {
      refetchSettlements();
    }
  }, [drawerParams.settlementId, refetchSettlements]);
  const [settlementPickerOpen, setSettlementPickerOpen] = useState(false);
  const [settlementPickerId, setSettlementPickerId] = useState<
    number | undefined
  >(undefined);
  // The real property behind the picked id — used to tell group vs unit.
  const { detail: pickedProperty } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: settlementPickerId,
  });

  const propLabel = (inv: Invoice) => {
    const id = inv.unitId ?? inv.groupId;
    const p = labelProperties.find(x => x.id === id);
    return p?.address ?? p?.code ?? id ?? "—";
  };

  // Which costs a confirmed settlement settled (→ "Opgenomen in afrekening"),
  // and which a departure settlement settled part of (→ "Deels afgerekend
  // (vertrek)").
  const settlementStates = useMemo(
    () => costSettlementStates(settlements),
    [settlements],
  );
  const settledInfoFor = (inv: Invoice) =>
    costSettledInfo(costSettlementStateOf(settlementStates, inv.id));

  const handleDelete = (inv: Invoice) => {
    // An OPEN payment invitation or order is tenant-facing: someone may be
    // about to pay a cost that would no longer exist. So deletion is blocked
    // until the broker consciously removes that payment under Betalingen —
    // the old flow warned "clean up yourself afterwards" and then deleted
    // anyway, leaving the tenant with a dangling open payment. Splits that
    // are already PAID don't block: there is nothing open to withdraw, and
    // the payment history stays under Betalingen. Judged by the live payment
    // status; the server has the last word (deleteRefusedMessage below).
    if (costsHaveOpenPayment({ invoices: [inv], statusMaps: costStatusMaps })) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_open_payment"),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    const doDelete = async () => {
      try {
        await remove(inv.id);
        refetch();
      } catch (error) {
        // Chained after the confirm dialog that is still closing.
        setTimeout(() =>
          confirm({
            type: "warning",
            title: getLocalizedText("costs.delete.title"),
            info: deleteRefusedMessage(error, "cost"),
            secondaryAction: {
              title: getLocalizedText("payment_request.button.ok"),
            },
          }),
        );
      }
    };
    // Product rule (Lewis, 2026-08-30): a cost inside a confirmed settlement
    // cannot be deleted — the settlement's frozen totals would diverge from
    // its cost list. Delete the settlement first, then the cost. Together
    // with the edit lock this makes a confirmed settlement untouchable.
    const settledInfo = settledInfoFor(inv);
    if (settledInfo.settled) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_settled"),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    // A departure settlement froze a tenant's share of the cost: it goes
    // first, the cost after it.
    const [departure] = settledInfo.departures ?? [];
    if (departure) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_departure", {
          name: departure.settlementName,
        }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    confirm({
      title: getLocalizedText("costs.delete.title"),
      info: getLocalizedText("costs.delete.confirm", {
        category: CATEGORY_LABELS_NL[inv.category],
        supplier: inv.supplier,
      }),
      primaryActions: [
        {
          title: getLocalizedText("costs.delete.title"),
          onPress: doDelete,
        },
      ],
    });
  };

  // To the cent: a float sum would print every stray decimal.
  const totalExcl = roundCents(
    invoices.reduce((s, i) => s + i.amountExclVat, 0),
  );

  const settlementPropertyLabel = (s: Settlement) => {
    const id = s.unitId ?? s.groupId;
    const p = labelProperties.find(x => x.id === id);
    return p?.address ?? p?.code ?? id ?? "—";
  };

  // The detail opens as a sidebar drawer (same pattern as the payment
  // details), not as a separate page.
  const openSettlement = (s: Settlement) => {
    setDrawerParams({ settlementId: s.id });
  };

  const handleDeleteSettlement = (s: Settlement) => {
    // Same rule as deleting a cost: an OPEN payment order or request created
    // from this settlement is tenant-facing, so the broker withdraws it under
    // Betalingen first — only then can the settlement itself be deleted. Paid
    // ones don't block.
    // A settlement with a payment is never deleted either (F07): both are
    // said before the broker is asked to confirm.
    const blocked = settlementDeleteBlockedMessage({
      settlement: s,
      statusMaps: settlementStatusMaps,
    });
    if (blocked) {
      confirm({
        type: "warning",
        title: getLocalizedText("settlement.delete.title"),
        info: blocked,
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    const doDelete = async () => {
      try {
        await removeSettlement(s.id);
      } catch (error) {
        // Chained after the confirm dialog that is still closing.
        setTimeout(() =>
          confirm({
            type: "warning",
            title: getLocalizedText("settlement.delete.title"),
            info: deleteRefusedMessage(error, "settlement"),
            secondaryAction: {
              title: getLocalizedText("payment_request.button.ok"),
            },
          }),
        );
      }
    };
    confirm({
      title: getLocalizedText("settlement.delete.title"),
      info: getLocalizedText("settlement.delete.confirm"),
      primaryActions: [
        {
          title: getLocalizedText("costs.action.delete"),
          onPress: doDelete,
        },
      ],
    });
  };

  // Geen concepten: alleen opgeslagen (bevestigde) afrekeningen in de lijst,
  // newest first.
  const confirmedSettlements = React.useMemo(
    () =>
      sortSettlementsNewestFirst(
        settlements.filter(s => s.status === "confirmed"),
      ),
    [settlements],
  );

  // The properties the payment requests live on (tenant units), for the live
  // payment status. For a group those are its child properties, and the
  // units each settlement froze for its tenants: an archived unit is gone
  // from the property list, its tenant's invitation is not.
  const settlementPropertyIds = React.useMemo(() => {
    const groupIds = new Set(
      confirmedSettlements.map(s => s.groupId).filter(Boolean),
    );
    return paymentRequestPropertyIds({
      propertyIds: assignable
        .filter(p => p.parentId != null && groupIds.has(p.parentId))
        .map(p => p.id),
      settlements: confirmedSettlements,
    });
  }, [confirmedSettlements, assignable]);
  const settlementStatusMaps = useSettlementPaymentStatus(
    confirmedSettlements,
    settlementPropertyIds,
  );

  const settlementRows: SettlementRowVM[] = confirmedSettlements.map(s => ({
    id: s.id,
    ...settlementRowTexts(s),
    // The property where a building's page has its Type column, as the
    // cost list above it does.
    propertyLabel: settlementPropertyLabel(s),
    payStatus: settlementTenantPaymentStatus(s, settlementStatusMaps),
    amount: settlementAmountTexts({ settlement: s, format: formatMoney }),
    onOpen: () => openSettlement(s),
    // The settlement of an archived property is only read until the property
    // is activated again (the property list holds active properties only).
    onDelete: assignable.some(p => p.id === (s.unitId ?? s.groupId))
      ? () => handleDeleteSettlement(s)
      : undefined,
  }));

  const handleCreateSettlement = async () => {
    if (!pickedProperty) return;
    const targetId = String(pickedProperty.id);
    setSettlementPickerOpen(false);
    // No DB row yet — the wizard's /add route seeds a local draft and only
    // creates a settlement on the first "Volgende" from the Periode step.
    navigate(`/properties/${targetId}/costs/settlements/add`);
  };

  // Starting an afrekening from the global costs view: here we don't know the
  // pand/groep yet, so ask it first (no pop-up). Only on "Volgende" is the
  // settlement actually created and the wizard opened. From a property page
  // (CostsTab) this step is skipped — the property is already known.
  if (settlementPickerOpen) {
    return (
      <Page title={getLocalizedText("costs.overview.new_settlement")}>
        <Card heading={getLocalizedText("costs.overview.property_or_group")}>
          <AutocompletePopperFix />
          <MuiStack direction="column">
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "12px",
                fontWeight: FontWeights.Regular,
                mb: 1,
              }}
            >
              {getLocalizedText("costs.overview.which_property")}
            </Typography>
            <PropertySelectInline
              value={settlementPickerId}
              onChange={id => setSettlementPickerId(id)}
            />
            <div style={{ marginTop: 32 }}>
              <ButtonGroup align="left">
                <Button
                  appearance="outline"
                  onClick={() => {
                    setSettlementPickerOpen(false);
                    setSettlementPickerId(undefined);
                  }}
                >
                  {getLocalizedText("costs.action.cancel")}
                </Button>
                <Button
                  appearance="primary"
                  isDisabled={!pickedProperty}
                  onClick={handleCreateSettlement}
                >
                  {getLocalizedText("costs.action.next")}
                </Button>
              </ButtonGroup>
            </div>
          </MuiStack>
        </Card>
      </Page>
    );
  }

  // A failed fetch replaces the list (see getListState).
  const costsListState = getListState({
    isLoading: isLoadingInvoices,
    loadFailed: invoicesLoadFailed,
    count: invoices.length,
  });
  const settlementsListState = getListState({
    isLoading: isLoadingSettlements,
    loadFailed: settlementsLoadFailed,
    count: confirmedSettlements.length,
  });

  return (
    <Page title={getLocalizedText("costs.overview.costs")}>
      <Stack vertical spacing="loose">
        <Card
          heading={getLocalizedText("costs.overview.all_costs")}
          subHeading={(() => {
            if (costsListState !== "list") return undefined;
            // With a search or filter active: what the list shows of all.
            if (costsView?.isFiltered) {
              return getLocalizedText(
                "costs.overview.count_filtered_of_total",
                {
                  count: String(costsView.count),
                  all: String(invoices.length),
                  total: fmt(costsView.totalExcl),
                },
              );
            }
            return getLocalizedText(
              invoices.length === 1
                ? "costs.overview.count_total_excl_vat_one"
                : "costs.overview.count_total_excl_vat_many",
              {
                count: String(invoices.length),
                total: fmt(totalExcl),
              },
            );
          })()}
          primaryActions={[
            {
              content: getLocalizedText("costs.action.add_cost"),
              onClick: () => setShowModal(true),
            },
          ]}
        >
          {(() => {
            if (costsListState === "load_failed") {
              return (
                <ListLoadFailed
                  heading={getLocalizedText("costs.overview.load_failed_costs")}
                  icon="billSplit"
                  onRetry={refetch}
                />
              );
            }
            if (costsListState === "loading") {
              return (
                <EmptyState
                  heading={getLocalizedText("costs.overview.loading_costs")}
                  icon="billSplit"
                />
              );
            }
            if (costsListState === "empty") {
              return (
                <EmptyState
                  heading={getLocalizedText("costs.overview.no_costs")}
                  icon="billSplit"
                >
                  {getLocalizedText("costs.overview.no_costs_assign_body")}
                </EmptyState>
              );
            }
            return (
              <CostsTable
                invoices={invoices}
                statusByOrderId={costStatusByOrderId}
                propertyLabelFor={propLabel}
                settledInfoFor={settledInfoFor}
                onView={openCostDrawer}
                onEdit={setEditInvoice}
                onDelete={handleDelete}
                onViewChange={handleCostsViewChange}
              />
            );
          })()}
        </Card>

        <Card
          heading={getLocalizedText("costs.overview.settlements")}
          primaryActions={[
            {
              content: getLocalizedText("costs.overview.add_settlement"),
              onClick: () => setSettlementPickerOpen(true),
            },
          ]}
        >
          {(() => {
            if (settlementsListState === "load_failed") {
              return (
                <ListLoadFailed
                  heading={getLocalizedText(
                    "costs.overview.load_failed_settlements",
                  )}
                  icon="billStack"
                  onRetry={refetchSettlements}
                />
              );
            }
            if (settlementsListState === "loading") {
              return (
                <EmptyState
                  heading={getLocalizedText(
                    "costs.overview.loading_settlements",
                  )}
                  icon="billStack"
                />
              );
            }
            if (settlementsListState === "empty") {
              return (
                <EmptyState
                  heading={getLocalizedText("costs.overview.no_settlements")}
                  icon="billStack"
                >
                  {getLocalizedText("costs.overview.no_settlements_body")}
                </EmptyState>
              );
            }
            return (
              <SettlementsTable
                rows={settlementRows}
                page={{ inBuilding: false, acrossProperties: true }}
              />
            );
          })()}
        </Card>
      </Stack>

      {showModal && (
        <InvoiceModal
          properties={assignable}
          onClose={() => setShowModal(false)}
          existingInvoices={invoices}
          onSaveMany={async payloads => {
            const stored: Invoice[] = [];
            for (const p of payloads) {
              // eslint-disable-next-line no-await-in-loop
              stored.push(await create(p));
            }
            refetch();
            return stored;
          }}
          // The popup adds every later tenant's payment to the cost it stored.
          onUpdate={async (id, payload) => {
            await update(id, payload);
            refetch();
          }}
        />
      )}

      {editInvoice && (
        <InvoiceModal
          properties={assignable}
          initialInvoice={editInvoice}
          departureShares={settledInfoFor(editInvoice).departures}
          onClose={() => setEditInvoice(null)}
          onSaveMany={() => undefined}
          onUpdate={async (id, payload) => {
            await update(id, payload);
            refetch();
          }}
        />
      )}
    </Page>
  );
};

// The global costs page follows the same rule as the property tab: the
// office's costs feature (useCostsEnabled).
export const CostsOverview: React.FC = () => {
  const enabled = useCostsEnabled();
  if (!enabled) return null;
  return <CostsOverviewInner />;
};
